import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { logsService, type ExportJobStatus } from '../services/logsService';
import type { TrackingSearchParams } from '../components/Logs/TrackingFilters';

// Estado do export com progresso
interface ExportState {
  isExporting: boolean;
  jobId: string | null;
  phase: string;
  progress: number;
  totalOperations: number;
  processedOperations: number;
  error: string | null;
  success: boolean;
  filename: string | null;
}

interface ExportContextType {
  exportState: ExportState;
  startExport: (params: TrackingSearchParams | null) => void;
  dismissExport: () => void;
}

const initialState: ExportState = {
  isExporting: false,
  jobId: null,
  phase: '',
  progress: 0,
  totalOperations: 0,
  processedOperations: 0,
  error: null,
  success: false,
  filename: null,
};

const ExportContext = createContext<ExportContextType | undefined>(undefined);

export const useExport = () => {
  const context = useContext(ExportContext);
  if (!context) {
    throw new Error('useExport must be used within ExportProvider');
  }
  return context;
};

// Download do blob
function downloadBlob(blob: Blob, filename: string) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

// Intervalo de polling (ms)
const POLL_INTERVAL = 1000;

export const ExportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [exportState, setExportState] = useState<ExportState>(initialState);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  // Limpa polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Poll status do job
  const pollJobStatus = useCallback(async (jobId: string) => {
    if (cancelledRef.current) return;

    try {
      const status: ExportJobStatus = await logsService.getExportJobStatus(jobId);

      if (cancelledRef.current) return;

      if (status.status === 'running' || status.status === 'pending') {
        // Atualiza progresso
        setExportState(prev => ({
          ...prev,
          phase: status.phase,
          progress: status.progress,
          totalOperations: status.totalOperations,
          processedOperations: status.processedOperations,
        }));

        // Continua polling
        pollingRef.current = setTimeout(() => pollJobStatus(jobId), POLL_INTERVAL);

      } else if (status.status === 'completed') {
        // Download do arquivo
        const blob = await logsService.downloadExportJob(jobId);
        const filename = status.filename || `operacoes_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.csv`;

        downloadBlob(blob, filename);

        setExportState({
          isExporting: false,
          jobId: null,
          phase: 'done',
          progress: 100,
          totalOperations: status.totalOperations,
          processedOperations: status.processedOperations,
          error: null,
          success: true,
          filename,
        });

      } else if (status.status === 'error') {
        setExportState({
          isExporting: false,
          jobId: null,
          phase: '',
          progress: 0,
          totalOperations: 0,
          processedOperations: 0,
          error: status.errorMessage || 'Erro desconhecido no export',
          success: false,
          filename: null,
        });
      }
    } catch (err) {
      console.error('Erro ao consultar status do export:', err);
      setExportState(prev => ({
        ...prev,
        isExporting: false,
        error: 'Erro ao consultar status do export',
        success: false,
      }));
    }
  }, []);

  const startExport = useCallback(async (params: TrackingSearchParams | null) => {
    // Para polling anterior se houver
    stopPolling();
    cancelledRef.current = false;

    setExportState({
      isExporting: true,
      jobId: null,
      phase: 'starting',
      progress: 0,
      totalOperations: 0,
      processedOperations: 0,
      error: null,
      success: false,
      filename: null,
    });

    try {
      const searchParams = params || {};

      // Inicia job no backend
      const response = await logsService.startExportJob(
        searchParams.dateFrom,
        searchParams.dateTo,
        searchParams.searchTerm,
        searchParams.externalId,
        searchParams.userType,
        searchParams.operation,
        searchParams.status
      );

      if (cancelledRef.current) return;

      setExportState(prev => ({
        ...prev,
        jobId: response.jobId,
        phase: 'fetching',
        progress: 5,
      }));

      // Inicia polling
      pollJobStatus(response.jobId);

    } catch (err) {
      console.error('Erro ao iniciar export:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao iniciar exportacao';
      setExportState({
        isExporting: false,
        jobId: null,
        phase: '',
        progress: 0,
        totalOperations: 0,
        processedOperations: 0,
        error: errorMessage,
        success: false,
        filename: null,
      });
    }
  }, [pollJobStatus, stopPolling]);

  const dismissExport = useCallback(() => {
    cancelledRef.current = true;
    stopPolling();
    setExportState(initialState);
  }, [stopPolling]);

  return (
    <ExportContext.Provider value={{ exportState, startExport, dismissExport }}>
      {children}
    </ExportContext.Provider>
  );
};
